/**
 * 설문 최종 제출의 완료 방어선 (TASK-2026-012 CR-01 §5~§7).
 *
 * 최종 답변을 저장한 뒤 `/complete`를 부르기 전에 이 세션의 기록이 **모두 서버 ACK를
 * 받았는지** 확인한다. 예전 경로는 동기화되지 않아도 그때의 `throughSeq`를 `last_seq`로
 * 보냈다. 서버는 자신이 받은 범위와 선언만 대조하므로, 로컬에 남은 더 높은 순번이 빠진
 * 채 완료가 확정될 수 있었다(로컬 재현: `last_seq=0`, 로컬 9번까지 미전송인데 완료).
 *
 * 지키는 것:
 * - `/complete`는 최신 상태가 완료 가능할 때 **한 번만**, 그때의 로컬 마지막 순번으로 보낸다.
 * - 조건을 못 채우면 `/complete`를 보내지 않는다. 자료는 전송기 대기열에 그대로 남는다.
 * - 참가자가 누른 제출은 명시적 동작이므로 온라인 표시를 넘어 실제로 보낸다(PR #35의
 *   `manual`). 실패하면 전송기가 알려 준 backoff만큼 기다리고, 제한 시간 안에서만 다시 본다.
 * - 차단·다른 탭 소유는 기다려도 풀리지 않으므로 곧바로 멈춘다.
 */

import type { Sender, SenderStatus } from '../online/sender';

/** 제출 한 번이 동기화를 기다리는 최대 시간. */
export const SUBMIT_SYNC_DEADLINE_MS = 30_000;
/** 결과가 `retry`가 아닌데 아직 동기화되지 않았을 때 다시 볼 간격. */
export const SUBMIT_RECHECK_MS = 1_000;

export type HoldReason = 'unsynced' | 'blocked' | 'readonly';

export type Readiness = { readonly ready: true; readonly lastSeq: number } | { readonly ready: false; readonly reason: HoldReason };

/**
 * 이 상태에서 `/complete`를 보내도 되는가.
 *
 * `synced`만으로는 부족하다 (CR-01 §6). 차단·다른 탭 소유는 따로 본다. 로컬 마지막 순번과
 * 서버 확인 순번이 같고, 보낼 이벤트·경로 조각·확인 기록·무결성 보고가 없어야 한다.
 */
export function completionReadiness(status: SenderStatus): Readiness {
  if (status.state === 'blocked') {
    return { ready: false, reason: 'blocked' };
  }
  if (status.state === 'readonly' || !status.holder) {
    return { ready: false, reason: 'readonly' };
  }
  if (
    !status.synced ||
    status.throughSeq !== status.localMaxSeq ||
    status.pendingEvents > 0 ||
    status.pendingChunks > 0 ||
    status.pendingConfirmations > 0
  ) {
    return { ready: false, reason: 'unsynced' };
  }
  return { ready: true, lastSeq: status.localMaxSeq };
}

export interface FinalizeDeps<T> {
  readonly sender: Sender;
  /** `/complete` 호출. 준비가 확인된 뒤 한 번만 부른다. */
  readonly complete: (lastSeq: number) => Promise<T>;
  readonly wait: (ms: number) => Promise<void>;
  readonly now: () => number;
  readonly deadlineMs?: number;
}

export type FinalizeOutcome<T> =
  | { readonly kind: 'submitted'; readonly lastSeq: number; readonly result: T }
  /** `/complete`를 보내지 않았다. 최종 답변은 이미 저장됐고 기록은 대기열에 있다. */
  | { readonly kind: 'held'; readonly reason: HoldReason; readonly status: SenderStatus; readonly lastError: string | null };

export async function finalizeSubmission<T>(deps: FinalizeDeps<T>): Promise<FinalizeOutcome<T>> {
  const deadline = deps.now() + (deps.deadlineMs ?? SUBMIT_SYNC_DEADLINE_MS);
  const held = (reason: HoldReason): FinalizeOutcome<T> => {
    const status = deps.sender.status();
    return { kind: 'held', reason, status, lastError: status.lastError };
  };

  for (;;) {
    /*
     * batch를 **하나씩** 보낸다 (REV-005). `flushAll`은 한 호출 안에서 최대 40개를 직렬로
     * 보내므로 그 사이에는 마감을 볼 수 없었다. 이제 batch마다 마감을 확인해, 제출 대기의
     * 상한은 30초에 이미 시작한 batch 하나(요청 제한 시간)를 더한 만큼이다.
     */
    const outcome = await deps.sender.flushOnce({ manual: true });
    // 보낸 뒤의 **최신** 상태로 판정한다. 앞선 반복에서 읽은 값을 쓰지 않는다.
    const readiness = completionReadiness(deps.sender.status());
    if (readiness.ready) {
      return { kind: 'submitted', lastSeq: readiness.lastSeq, result: await deps.complete(readiness.lastSeq) };
    }
    if (readiness.reason !== 'unsynced') {
      return held(readiness.reason);
    }
    if (outcome.kind === 'blocked') {
      return held('blocked');
    }
    if (outcome.kind === 'not_holder') {
      return held('readonly');
    }
    if (outcome.kind === 'sent') {
      // 더 보낼 것이 있다. 마감이 지났으면 다음 batch를 시작하지 않는다. 자료는 대기열에 남는다.
      if (deps.now() >= deadline) {
        return held('unsynced');
      }
      continue;
    }
    const wait = outcome.kind === 'retry' ? outcome.retryAfterMs : SUBMIT_RECHECK_MS;
    if (deps.now() + wait > deadline) {
      return held('unsynced');
    }
    await deps.wait(wait);
  }
}
